import currency from 'currency.js'
import round from 'lodash/round'
import { numToyearsAndMonths } from '../../../../helpers/StringHelper'
import { CalculatorDataAsNumbers } from '../types/RoiCalculatorTypes'
import I18n from '../../../../helpers/I18n'

export const toNumber = (stringNum: string | number) =>
  typeof stringNum === 'string' ? Number(stringNum.split(',').join('')) : stringNum

const currencyOptions = { precision: 8 }

export const calculateMultiplication = (...args) => {
  const currencyNums = args.map((stringNum?: string | number) =>
    currency(stringNum || 0, currencyOptions)
  )

  return Number(currencyNums.reduce((n1, n2) => n1.multiply(n2)))
}

export type CalculatorResults = [
  totalPaybackTime: string | undefined,
  unitsToBreakEven: number,
  fiveYearSavings: number,
  cumulativeValues: {
    savings: number[]
    units: number[]
  },
  timeToBreakEvenInYears: number
]

export const calculateRoiOutput = (
  calculatorDataAsNumbers: Required<CalculatorDataAsNumbers>
): CalculatorResults => {
  const processesIncluded =
    calculatorDataAsNumbers.manualProcess > 0 && calculatorDataAsNumbers.automatedProcess > 0

  const totalWorkerShiftsPerDay =
    calculatorDataAsNumbers.shiftsPerDay *
    calculatorDataAsNumbers.numberOfWorkersPerShift;

  const dailyProductionWithAutoEquip =
    ((calculatorDataAsNumbers.hoursPerShift * totalWorkerShiftsPerDay * 3600) /
      calculatorDataAsNumbers.automatedProcess) *
    calculatorDataAsNumbers.estimatedEquipmentEfficiency

  const currentProcessCost =
    ((calculatorDataAsNumbers.labourRate / 3600) * calculatorDataAsNumbers.manualProcess) /
    calculatorDataAsNumbers.estimatedWorkerEfficiency

  let unitsToBreakEven: number | undefined

  if (processesIncluded) {
    unitsToBreakEven = round(
      calculatorDataAsNumbers.equipmentHardwareCost / currentProcessCost || 0
    )
  } else {
    unitsToBreakEven = 0
  }

  let timeToBreakEven: number

  if (processesIncluded) {
    timeToBreakEven =
      (unitsToBreakEven /
        (dailyProductionWithAutoEquip * (calculatorDataAsNumbers.workDaysPerWeek / 7)) /
        365) *
      12
  } else {
    timeToBreakEven =
      (calculatorDataAsNumbers.equipmentHardwareCost /
        (calculatorDataAsNumbers.labourRate *
          calculatorDataAsNumbers.hoursPerShift *
          calculatorDataAsNumbers.workDaysPerWeek *
          totalWorkerShiftsPerDay *
          52)) *
      12
  }

  let totalPaybackTime: string | undefined

  if (timeToBreakEven && timeToBreakEven >= 1) {
    totalPaybackTime = numToyearsAndMonths(round(timeToBreakEven))
  } else if (timeToBreakEven && timeToBreakEven % 1 != 0) {
    totalPaybackTime = `< 1 ${I18n.t('datetime.units.month')}`
  }

  const savingsCalculationWithCycleTime =
    currentProcessCost *
    dailyProductionWithAutoEquip *
    (calculatorDataAsNumbers.workDaysPerWeek / 7) *
    365

  const savingsCalculationWithoutCycleTime =
    calculatorDataAsNumbers.labourRate *
    calculatorDataAsNumbers.hoursPerShift *
    calculatorDataAsNumbers.workDaysPerWeek *
    totalWorkerShiftsPerDay *
    52

  let savingsCalculation

  if (calculatorDataAsNumbers.automatedProcess > 0 && calculatorDataAsNumbers.manualProcess > 0) {
    savingsCalculation = savingsCalculationWithCycleTime
  } else {
    savingsCalculation = savingsCalculationWithoutCycleTime
  }

  const unitsCalculation = round(
    dailyProductionWithAutoEquip * (calculatorDataAsNumbers.workDaysPerWeek / 7) * 365 || 0
  )

  const oneYearUnits = unitsCalculation * 1
  const twoYearUnits = unitsCalculation * 2
  const threeYearUnits = unitsCalculation * 3
  const fourYearUnits = unitsCalculation * 4
  const fiveYearUnits = unitsCalculation * 5

  const oneYearSavings: number = savingsCalculation - calculatorDataAsNumbers.equipmentHardwareCost
  const twoYearSavings: number = savingsCalculation + oneYearSavings
  const threeYearSavings: number = savingsCalculation + twoYearSavings
  const fourYearSavings: number = savingsCalculation + threeYearSavings
  const fiveYearSavings: number = savingsCalculation + fourYearSavings

  const cumulativeValues = {
    savings: [oneYearSavings, twoYearSavings, threeYearSavings, fourYearSavings, fiveYearSavings],
    units: [oneYearUnits, twoYearUnits, threeYearUnits, fourYearUnits, fiveYearUnits],
  }

  // round to 2 decimals
  const timeToBreakEvenInYears = parseFloat((timeToBreakEven / 12).toFixed(2))

  return [
    totalPaybackTime,
    unitsToBreakEven,
    fiveYearSavings,
    cumulativeValues,
    timeToBreakEvenInYears,
  ]
}
